'use client'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { Reveal, BlurReveal } from '@/components/motion-primitives'
import { Icon } from '@/components/icon'
import { WaveBackground } from '@/components/wave-background'
import { cn } from '@/lib/utils'

type Feat = {
  title: string
  body: string
  icon: string
  href: string
  span?: string
  feature?: boolean
}

const FEATURES: Feat[] = [
  {
    title: 'Payment orchestration',
    body: 'Route every transaction across the optimal rail automatically — by cost, speed and reliability.',
    icon: 'Workflow',
    href: '/products/orchestration',
    span: 'lg:col-span-2 lg:row-span-2',
    feature: true,
  },
  { title: 'Wallets', body: 'Hold and manage fiat and stablecoins.', icon: 'Wallet', href: '/products/wallets' },
  { title: 'Stablecoins', body: 'Modular stablecoin infrastructure.', icon: 'Bitcoin', href: '/solutions/crypto-defi' },
  { title: 'Virtual accounts', body: 'Receive payments locally worldwide.', icon: 'CreditCard', href: '/products/virtual-accounts' },
  { title: 'Collections', body: 'Collect local payments globally.', icon: 'Download', href: '/solutions/payment-collections' },
  {
    title: 'Developer APIs',
    body: 'Clean, well-documented REST APIs and SDKs for every language.',
    icon: 'Code2',
    href: '/products/payment-apis',
    span: 'lg:col-span-2',
    feature: true,
  },
  { title: 'Payouts', body: 'Pay anyone, anywhere, at scale.', icon: 'Send', href: '/solutions/global-payouts' },
  { title: 'Fraud prevention', body: 'Real-time monitoring and controls.', icon: 'ShieldCheck', href: '/products/compliance-engine' },
  { title: 'Compliance', body: 'KYC, KYB and AML built in.', icon: 'Fingerprint', href: '/products/compliance-engine' },
  { title: 'Settlement', body: 'Fast, deterministic settlement.', icon: 'Zap', href: '/products/settlement-engine' },
  { title: 'Analytics', body: 'Reporting and payment insights.', icon: 'BarChart3', href: '/products/analytics' },
  { title: 'Global banking', body: 'Local rails and bank integrations.', icon: 'Building2', href: '/solutions/banking-infrastructure' },
]

export function Features() {
  return (
    <section className="relative overflow-hidden bg-background py-24">
      <WaveBackground position="middle" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand">Platform</p>
          <h2 className="mt-3 text-balance text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            One platform for every way you move money
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
            From orchestration to settlement, Rombex gives you the building blocks to operate
            globally and build the next generation of financial products.
          </p>
        </Reveal>

        {/* Payment Orchestration card — featured */}
        <BlurReveal className="mt-14">
          <Link
            href={FEATURES.find(f => f.title === 'Payment orchestration')?.href || '/products/orchestration'}
            className="group relative flex h-96 overflow-hidden rounded-3xl border border-brand/30 transition-all hover:shadow-2xl hover:shadow-brand/20"
          >
            <img
              src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/interac-trans-image-lVQOEGjTYmcXKrga2EHqXIjH0UENUU.jpg"
              alt="Happy customers celebrating"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-brand/95 via-brand/70 to-transparent" />
            <div className="relative z-10 flex flex-col justify-end p-8">
              <span className="flex size-12 items-center justify-center rounded-xl bg-white/15 text-brand-foreground mb-4 w-fit">
                <Icon name="Zap" className="size-6" />
              </span>
              <h3 className="text-3xl font-bold text-brand-foreground">Payment orchestration</h3>
              <p className="mt-2 max-w-md text-brand-foreground/90">
                Seamlessly route payments through optimal corridors.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-semibold text-brand-foreground">
                Learn more
                <ArrowUpRight className="size-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </div>
          </Link>
        </BlurReveal>

        {/* Features highlights — minimal list style */}
        <div className="mt-24 grid gap-16">
          <Reveal>
            <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-3">
              {FEATURES.filter(f => f.title !== 'Payment orchestration').map((f, i) => (
                <Reveal key={f.title} delay={i * 0.1}>
                  <Link href={f.href} className="group">
                    <span className="flex size-12 items-center justify-center rounded-lg bg-brand/10 text-brand transition-all group-hover:bg-brand group-hover:text-brand-foreground mb-4">
                      <Icon name={f.icon} className="size-6" />
                    </span>
                    <h3 className="text-xl font-semibold text-foreground">{f.title}</h3>
                    <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{f.body}</p>
                    <span className="mt-4 inline-flex items-center gap-2 font-medium text-brand opacity-0 transition-opacity group-hover:opacity-100">
                      Explore
                      <ArrowUpRight className="size-4" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}
